import { useState, useRef, useEffect } from 'react';
import { 
  Send, Sparkles, Loader2, Zap, ChevronRight,
  Lightbulb, Target, TrendingUp, MessageSquare, DollarSign, Clock,
  CheckCircle, AlertCircle, Star, Heart, Briefcase, Users, FileText, BarChart,
  type LucideIcon
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAIHelper } from '@/lib/hooks/use-ai-helper';
import { useUserPreferences } from '@/lib/hooks/use-user-preferences';
import { chatService } from '@/lib/api/services/chat.service';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';

type Tip = {
  title: string;
  icon: string;
  content: string;
};

const iconMap: Record<string, LucideIcon> = {
  Zap,
  Lightbulb,
  Target,
  TrendingUp,
  MessageSquare,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  Heart,
  Briefcase,
  Users,
  FileText,
  BarChart,
};

interface AIHelperProps {
  conversationId?: string | null;
  receivedMessageCount?: number; // Track received messages to refresh tips
}

export function AIHelper({ conversationId, receivedMessageCount = 0 }: AIHelperProps) {
  const { preferences, updatePreferences, isLoading: preferencesLoading } = useUserPreferences();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(!preferences.helper_collapsed);
  const [inputValue, setInputValue] = useState('');
  const { messages, askQuestion, isPending, clearHistory } = useAIHelper(conversationId);
  const helperChatRef = useRef<HTMLDivElement>(null);
  const [contextualTips, setContextualTips] = useState<Tip[]>([]);
  const [tipsLoading, setTipsLoading] = useState(false);
  const lastReceivedMessageCountRef = useRef<number>(0);
  const isFetchingTipsRef = useRef<boolean>(false);
  const lastConversationIdRef = useRef<string | null | undefined>(conversationId);

  // Sync isOpen with DB state
  useEffect(() => {
    if (!preferencesLoading) {
      setIsOpen(!preferences.helper_collapsed);
    }
  }, [preferences.helper_collapsed, preferencesLoading]);

  // Update DB when isOpen changes (optimistically)
  const handleToggleOpen = (newState: boolean) => {
    const newCollapsedState = !newState;
    
    // Optimistically update the UI immediately
    type UserPreferences = {
      id?: string;
      user_id?: string;
      tips_enabled: boolean;
      helper_collapsed: boolean;
      suggestions_collapsed: boolean;
      created_at?: string;
      updated_at?: string;
    };
    
    queryClient.setQueryData<UserPreferences>(['user-preferences'], (old) => ({
      ...(old || {
        tips_enabled: true,
        helper_collapsed: true,
        suggestions_collapsed: true,
      }),
      helper_collapsed: newCollapsedState,
    }));
    
    // Update local state
    setIsOpen(newState);
    
    // Save preference in the background (don't await)
    updatePreferences({ helper_collapsed: newCollapsedState }).catch((error) => {
      console.error('Failed to save helper preference:', error);
      // Revert on error
      queryClient.setQueryData<UserPreferences>(['user-preferences'], (old) => ({
        ...(old || {
          tips_enabled: true,
          helper_collapsed: true,
          suggestions_collapsed: true,
        }),
        helper_collapsed: !newCollapsedState,
      }));
      setIsOpen(!newState);
    });
  };


  const scrollHelperToBottom = () => {
    if (helperChatRef.current) {
      helperChatRef.current.scrollTop = helperChatRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollHelperToBottom();
  }, [messages, isPending]);

  // Fetch contextual tips only if helper is open and not collapsed
  useEffect(() => {
    // Reset fetching flag when conversation changes
    if (lastConversationIdRef.current !== conversationId) {
      isFetchingTipsRef.current = false;
      lastConversationIdRef.current = conversationId;
    }
    
    // Don't fetch tips if helper is collapsed
    if (!isOpen) {
      setContextualTips([]);
      setTipsLoading(false);
      lastReceivedMessageCountRef.current = 0;
      isFetchingTipsRef.current = false;
      return;
    }

    // Initialize ref on first load or conversation change
    if (lastReceivedMessageCountRef.current === 0 && receivedMessageCount > 0) {
      lastReceivedMessageCountRef.current = receivedMessageCount;
    }

    // Check if a new message was received (count increased)
    const newMessageReceived = receivedMessageCount > lastReceivedMessageCountRef.current;
    
    // Fetch tips when:
    // 1. Helper is opened and no messages in helper chat yet, OR
    // 2. A new message was received (only if helper is open and no helper messages)
    if (conversationId && messages.length === 0) {
      // Only fetch if tips are empty or new message received, and we're not already fetching
      if ((contextualTips.length === 0 || newMessageReceived) && !isFetchingTipsRef.current) {
        if (newMessageReceived) {
          lastReceivedMessageCountRef.current = receivedMessageCount;
          // Clear existing tips immediately when new message received to show loading state
          setContextualTips([]);
        }
        // Set loading state and flag before API call
        setTipsLoading(true);
        isFetchingTipsRef.current = true;
        chatService
          .getContextualTips(conversationId)
          .then((tips) => {
            // Tips are already limited to 4 by backend
            setContextualTips(tips);
            setTipsLoading(false);
            isFetchingTipsRef.current = false;
          })
          .catch((error) => {
            console.error('Failed to fetch tips:', error);
            setTipsLoading(false);
            isFetchingTipsRef.current = false;
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, conversationId, messages.length, receivedMessageCount]);

  const handleSend = () => {
    if (inputValue.trim() && !isPending) {
      askQuestion(inputValue.trim());
      setInputValue('');
    }
  };

  // Open state - show sidebar
  return (
    <div className="w-80 border-l border-slate-200 bg-white flex flex-col h-full">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#4669ed]" />
          <h3 className="font-bold text-slate-900">AI Helper</h3>
        </div>
        <div className="flex items-center gap-3">
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearHistory}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </Button>
          )}
          <button
            onClick={() => handleToggleOpen(false)}
            className="p-1 rounded-lg transition hover:bg-slate-100"
          >
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      <div ref={helperChatRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Contextual Tips */}
        {messages.length === 0 && (
          <>
            {conversationId && tipsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8">
                <Loader2 className="h-5 w-5 animate-spin text-[#4669ed]" />
                <span className="text-sm text-slate-500">
                  Loading tips...
                </span>
              </div>
            ) : conversationId && contextualTips.length > 0 ? (
              <>
                <p className="text-xs mb-3 text-slate-500">
                  Tips based on your conversation:
                </p>
                {contextualTips.map((tip, index) => {
                  const IconComponent = iconMap[tip.icon] || Zap;
                  return (
                    <button
                      key={index}
                      onClick={() => askQuestion(tip.content)}
                      className="w-full text-left rounded-2xl border p-4 transition-all cursor-pointer bg-white border-slate-200 hover:shadow-lg hover:border-[#4669ed]/30"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#4669ed]/10">
                          <IconComponent className="w-5 h-5 text-[#4669ed]" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-slate-900 mb-1">
                            {tip.title}
                          </h4>
                          <p className="text-sm text-slate-600">
                            {tip.content}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </>
            ) : null}
          </>
        )}

        {/* Messages */}
        {messages.length > 0 && (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {message.role === 'user' ? (
                  <div className="bg-[#4669ed] text-white px-4 py-2 rounded-2xl rounded-br-md max-w-[85%]">
                    <p className="text-sm">{message.content}</p>
                  </div>
                ) : (
                  <div className="max-w-[95%] bg-slate-100">
                    <div className="rounded-2xl rounded-bl-md p-4 bg-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-[#4669ed]" />
                        <span className="text-sm font-semibold text-[#4669ed]">AI Response</span>
                      </div>
                      <div className="text-sm leading-relaxed prose prose-sm max-w-none text-slate-700">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="ml-2">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            code: ({ children }) => (
                              <code className="bg-slate-200 dark:bg-slate-600 px-1 py-0.5 rounded text-xs">
                                {children}
                              </code>
                            ),
                            h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {isPending && (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="h-5 w-5 animate-spin text-[#4669ed]" />
            <span className="text-sm text-slate-500">
              AI is thinking...
            </span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-200">
        <div className="relative">
          <Input
            placeholder="Ask AI for advice..."
            className="pr-10 rounded-full bg-slate-50 border-slate-200"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            disabled={isPending}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isPending}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-[#4669ed] text-white rounded-full hover:bg-[#2b42a3] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
